import { useEffect, useMemo, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR from 'swr';
import { Loader2 } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { patchCheckoutQueue } from '@/api/checkoutControl';
import { patchTable } from '@/api/tables';
import {
  ANSWER_COLOR, ANSWER_LABEL, ANSWER_ORDER,
  TRACK_ANSWER_COLOR, TRACK_ANSWER_LABEL, TRACK_ANSWER_ORDER, getTrackChart, postQueueRecount,
  type QueueRecountAlert, type TrackChartPhoto, type TrackChartResponse,
} from '@/api/calibrationChart';
import { recountBadge, summarizeRecountAlerts } from './queueRecount';
import DistributionChart, { type DistBin, type DistMarker } from './DistributionChart';
import { ChartPeriod, daysAgo, today, windowParams } from './ChartPeriod';
import { Tooltip } from '@/components/ui/tooltip';

// AI197 #1168/#1182: распределение стола (скор «грязный», системный порог справочно), очереди
// (людей за минуту, порог max_allowed_size — перетаскивается) и стеллажа (процент по модели,
// статусы ответа на алерт стеллажа) с фото колонками над бинами.
//
// Очередь (Алексей 21.10): «Пересчёт по полигону» — та же гистограмма, но число людей заново
// посчитано по сохранённым боксам под текущий (или черновой, ещё не сохранённый) полигон; на фото
// плашки «было → стало», у кадров высланных алертов — ушёл бы алерт по новому полигону.

type Kind = 'table' | 'queue' | 'rack';
const TRACK_LEGEND = Object.fromEntries(TRACK_ANSWER_ORDER.map((k) => [k, { label: TRACK_ANSWER_LABEL[k], color: TRACK_ANSWER_COLOR[k] }]));
const RACK_LEGEND = Object.fromEntries(ANSWER_ORDER.map((k) => [k, { label: ANSWER_LABEL[k], color: ANSWER_COLOR[k] }]));
const LEGEND_ALL: Record<string, { label: string; color: string }> = { ...TRACK_LEGEND, ...RACK_LEGEND };

function fmtDt(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const pct = (v: number) => `${Math.round(v * 100)}%`;
function caption(kind: Kind, p: TrackChartPhoto, alert?: QueueRecountAlert): string {
  const x = kind === 'table' ? `уверенность ${pct(p.x)}${p.state ? ` · ${p.state}` : ''}`
    : kind === 'queue'
      ? (p.x_orig != null && p.x_orig !== p.x ? `было ${p.x_orig} → по новому полигону ${p.x} чел.` : `${p.x} чел.`)
      : `заполненность ${pct(p.x)}`;
  const minute = p.minute_x_orig != null && p.minute_x != null && p.minute_x_orig !== p.minute_x
    ? ` · максимум минуты ${p.minute_x_orig} → ${p.minute_x}` : '';
  const verdict = alert
    ? ` · кадр алерта: ${alert.would_fire === false ? 'по новому полигону НЕ ушёл бы' : alert.would_fire ? 'ушёл бы и по новому' : 'порога нет'}`
    : '';
  return `${x}${minute} · ${fmtDt(p.at)} · ${LEGEND_ALL[p.answer]?.label ?? p.answer}${verdict}`;
}

/** Сводка пересчёта: сколько кадров/минут поменялось и что стало с высланными алертами. */
function RecountSummary({ data }: { data: TrackChartResponse }) {
  const rc = data.recount;
  if (!rc) return null;
  const sum = summarizeRecountAlerts(rc.alerts);
  const { lost, changed: changedAlerts } = sum;
  const unknownSize = Object.values(rc.frame_size).some((sz) => !sz.known);
  return (
    <div className="space-y-1 rounded-md border border-border bg-muted/40 p-2 text-[11px] text-muted-foreground">
      <div>
        <span className="font-medium text-foreground">Пересчёт по боксам {rc.draft ? '(черновик полигона, не сохранён)' : '(текущий полигон)'}:</span>{' '}
        {rc.frames} кадров с {fmtDt(rc.from)}{rc.truncated ? ` — только последние ${rc.max_frames} кадров периода` : ''};
        число изменилось на <b className="text-foreground">{rc.frames_changed}</b> кадрах, в {rc.minutes_changed} минутах.
        {' '}Боксы людей с уверенностью от {Math.round(rc.score_threshold * 100)}%, ноги (низ бокса) в полигоне — как при съёмке.
        {rc.no_boxes > 0 && ` У ${rc.no_boxes} кадров боксы не сохранились — взято записанное число.`}
        {unknownSize && ' Размер кадра камеры не прочитался — считали как 1920×1080, у самой границы полигона возможна ошибка в 1 человека.'}
      </div>
      {sum.total > 0 && (
        <div>
          <span className="font-medium text-foreground">Высланные алерты «длинная очередь»: {sum.total}.</span>{' '}
          По новому полигону ушли бы {sum.wouldFire},{' '}
          <span className={lost.length ? 'font-medium text-red-600 dark:text-red-400' : ''}>не ушли бы {lost.length}</span>
          {lost.length > 0 && ` (${TRACK_ANSWER_LABEL.confirmed}: ${sum.lostBy.confirmed}, ${TRACK_ANSWER_LABEL.cancelled}: ${sum.lostBy.cancelled}, без ответа: ${sum.lostBy.pending})`}.
          {' '}Порог — текущий; стабильность — как у алерта (то же число на предыдущем кадре).
        </div>
      )}
      {changedAlerts.length > 0 && (
        <ul className="grid gap-x-4 sm:grid-cols-2">
          {changedAlerts.slice(0, 12).map((a) => (
            <li key={a.at} className="tabular-nums">
              {fmtDt(a.at)} · <span style={{ color: TRACK_ANSWER_COLOR[a.answer] }}>{TRACK_ANSWER_LABEL[a.answer]}</span>
              {' · '}{a.orig} → {a.new} чел.
              {' · '}{a.would_fire === false ? <b className="text-red-600 dark:text-red-400">не ушёл бы</b> : a.would_fire ? 'ушёл бы' : '—'}
            </li>
          ))}
          {changedAlerts.length > 12 && <li>… ещё {changedAlerts.length - 12}</li>}
        </ul>
      )}
    </div>
  );
}

function StatesTable({ data }: { data: TrackChartResponse }) {
  if (!data.states) return null;
  const cols = TRACK_ANSWER_ORDER.filter((k) => data.states!.some((s) => (s.counts[k] ?? 0) > 0));
  if (cols.length === 0) return null;
  return (
    <table className="text-[11px]">
      <thead>
        <tr className="text-muted-foreground">
          <th className="pr-3 text-left font-medium">Состояние по модели</th>
          {cols.map((k) => <th key={k} className="pr-3 text-right font-medium" style={{ color: TRACK_ANSWER_COLOR[k] }}>{TRACK_ANSWER_LABEL[k]}</th>)}
        </tr>
      </thead>
      <tbody>
        {data.states.map((s) => (
          <tr key={s.state}>
            <td className="pr-3 text-foreground">{s.label}</td>
            {cols.map((k) => <td key={k} className="pr-3 text-right tabular-nums">{s.counts[k] ?? 0}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function TrackDistributionChart({ kind, entityId, thresholdEntityId, onChanged, onThresholdsPreview, score, title, queueRecount }: {
  kind: Kind;
  entityId: number;
  /** Очередь: пересчёт по боксам. draft — черновой полигон {area_id: точки} (null — сохранённый);
   *  каждый новый nonce (сохранили полигон / «пересчитать по черновику») включает режим пересчёта. */
  queueRecount?: { draft: Record<string, number[][]> | null; nonce: number } | null;
  /** Стол: скор по оси X — «еда», «грязная посуда» (две гистограммы, Алексей #1376) или
   *  «нативные объекты» (#1516; на статус и алерт не влияют — маркера порога нет). */
  score?: 'food' | 'dirty' | 'native';
  /** Заголовок над графиком (когда графиков несколько). */
  title?: string;
  /** Очередь: id CheckoutQueue; стол: id Table — для сохранения перетащенного порога. */
  thresholdEntityId?: string | null;
  onChanged?: () => void;
  /** Отпустили маркер — новый порог сразу в поля плашки, не дожидаясь перезагрузки списка (#7022). */
  onThresholdsPreview?: (next: Record<string, number | boolean>) => void;
}) {
  // Стол: по умолчанию все замеры — алертов со скорами мало, а смысл графика — распределение
  // уверенности модели относительно порога (AI212, Алексей #1299 «гистограмму как у полок»).
  const [mode, setMode] = useState<'alerts' | 'all'>(kind === 'table' ? 'all' : 'alerts');
  const [from, setFrom] = useState(daysAgo(14));
  const [to, setTo] = useState(today());
  const win = windowParams(from, to);
  const [source, setSource] = useState<'stored' | 'recount'>('stored');
  const nonce = queueRecount?.nonce ?? 0;
  useEffect(() => { if (nonce > 0) setSource('recount'); }, [nonce]);
  const recount = kind === 'queue' && source === 'recount';
  const draft = recount ? queueRecount?.draft ?? null : null;
  const { data, isLoading, error, mutate } = useSWR(
    recount
      ? ['calibration:queue-recount', entityId, mode, win.from, win.to, draft ? JSON.stringify(draft) : '', nonce]
      : ['calibration:track-chart', kind, entityId, mode, win.from, win.to, score ?? ''],
    () => (recount
      ? postQueueRecount({ id: entityId, mode, ...win, polygons: draft })
      : getTrackChart({ kind, id: entityId, mode, ...win, ...(score ? { score } : {}) })).then((r) => r.data),
    { revalidateOnFocus: false },
  );
  const [saving, setSaving] = useState(false);
  const alertByFrame = useMemo(() => {
    const m = new Map<number, QueueRecountAlert>();
    for (const a of data?.recount?.alerts ?? []) if (a.frame_id != null) m.set(a.frame_id, a);
    return m;
  }, [data]);
  const bins: DistBin[] = useMemo(() => (data?.bins ?? []).map((b) => ({
    lo: b.lo, hi: b.hi, counts: b.counts,
    label: kind === 'queue' ? `${b.label} чел.` : kind === 'rack' ? `${pct(b.lo)}–${pct(b.hi)}` : (b.label ?? undefined),
    photos: b.photos.map((p) => {
      const alert = p.is_alert_frame ? alertByFrame.get(p.frame_id) : undefined;
      return {
        key: `${p.frame_id}-${p.at}`, url: p.url, caption: caption(kind, p, alert), answer: p.answer,
        boxes: p.boxes, polygons: p.polygons,
        badge: data?.recount ? recountBadge(p, alert) : null,
      };
    }),
  })), [data, kind, alertByFrame]);
  const legend = kind === 'rack' ? RACK_LEGEND : TRACK_LEGEND;
  const order = kind === 'rack' ? ANSWER_ORDER : TRACK_ANSWER_ORDER;
  const markers: DistMarker[] = [];
  if (data && data.threshold != null) {
    markers.push({
      key: 'thr', x: data.threshold, color: '#dc2626', label: data.threshold_label,
      min: kind === 'queue' ? 0.5 : kind === 'table' ? 0.05 : 0, max: kind === 'queue' ? 50 : kind === 'table' ? 0.99 : 1,
      onChange: kind === 'queue' && thresholdEntityId
        ? async (x) => {
          setSaving(true);
          try {
            const size = Math.max(0, Math.round(x - 0.5));
            onThresholdsPreview?.({ max_allowed_size: size });
            await patchCheckoutQueue(thresholdEntityId, { max_allowed_size: size });
            await mutate();
            onChanged?.();
          } finally { setSaving(false); }
        }
        : kind === 'table' && thresholdEntityId
          // Стол: перетащенный маркер = свой порог алерта (AI214, #1306).
          ? async (x) => {
            setSaving(true);
            try {
              const conf = Math.round(Math.min(0.99, Math.max(0.05, x)) * 100) / 100;
              onThresholdsPreview?.({ alert_min_dirty_conf: conf, alert_threshold: conf });
              await patchTable(thresholdEntityId, { alert_min_dirty_conf: conf });
              await mutate();
              onChanged?.();
            } finally { setSaving(false); }
          }
          : undefined,
    });
  }

  return (
    <div className="space-y-2">
      {title && <div className="text-sm font-medium">{title}</div>}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <ToggleGroup value={mode} onChange={(v) => v && setMode(v as 'alerts' | 'all')}>
          <ToggleGroupItem value="alerts">{kind === 'queue' ? 'Минуты алертов' : 'Кадры алертов'}</ToggleGroupItem>
          <ToggleGroupItem value="all">Все замеры</ToggleGroupItem>
        </ToggleGroup>
        <ChartPeriod from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
        {kind === 'queue' && (
          <ToggleGroup value={source} onChange={(v) => v && setSource(v as 'stored' | 'recount')}>
            <ToggleGroupItem value="stored">Как записано</ToggleGroupItem>
            <ToggleGroupItem value="recount">{queueRecount?.draft ? 'Пересчёт по черновику' : 'Пересчёт по полигону'}</ToggleGroupItem>
          </ToggleGroup>
        )}
        {data && <Tooltip content={data.threshold_note}><span className="ml-auto text-muted-foreground">{data.x_label}</span></Tooltip>}
      </div>
      {isLoading && <div className="flex h-24 items-center justify-center"><Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden /></div>}
      {error && !data && <ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} />}
      {data && (
        <>
          {kind === 'table' && !data.scores_from && (
            <div className="text-[11px] text-muted-foreground">
              Скоры модели пишутся с 29.09 — распределение по скору появится по мере накопления; пока только распределение по состояниям.
            </div>
          )}
          {kind === 'table' && data.stats.scored === 0 && <StatesTable data={data} />}
          {recount && <RecountSummary data={data} />}
          <DistributionChart
            bins={bins} legend={legend} order={order} markers={markers}
            belowIsBad={false}
            xTickFormatter={kind === 'queue' ? (v) => String(Math.round(v + 0.5)) : kind === 'rack' ? pct : (v) => v.toFixed(2)}
            axisMin={kind === 'queue' ? -0.5 : 0} axisMax={kind === 'queue' ? 50.5 : 1}
            emptyText={kind === 'table' && data.stats.scored === 0 ? 'Замеров со скорами пока нет.' : 'Нет замеров за выбранный период.'}
          />
          {kind === 'table' && (data.stats.scored ?? 0) > 0 && <StatesTable data={data} />}
          <div className="text-[11px] text-muted-foreground">
            {saving && <span className="mr-2 text-foreground"><Loader2 className="mr-1 inline size-3 animate-spin" aria-hidden />сохраняю порог…</span>}
            Замеров: {data.stats.n} · с ответом на алерт: {data.stats.answered}
            {data.stats.above != null && ` · выше порога: ${data.stats.above}`}
            {data.stats.above_orig != null && data.stats.above_orig !== data.stats.above && ` (как записано: ${data.stats.above_orig})`}
            {data.stats.scored != null && ` · со скорами: ${data.stats.scored}`}
            {kind === 'table' && data.stats.uncertain_share != null && data.stats.uncertain_band && (
              <span className={data.stats.unconfident ? ' font-medium text-amber-700 dark:text-amber-300' : ''}>
                {` · в серой зоне ${pct(data.stats.uncertain_band[0])}–${pct(data.stats.uncertain_band[1])} (ни «чисто», ни «грязно»): ${pct(data.stats.uncertain_share)} кадров`}
                {data.stats.unconfident ? ' — предсказывается неуверенно' : ''}
              </span>
            )}
            {kind === 'table' && ` · ${data.threshold_note}`}
          </div>
        </>
      )}
    </div>
  );
}
